import { faChevronLeft, faChevronRight, faSearchPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { Props } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhotoPage";
import {
    ArticleGalleryItem,
    buildArticleGalleryPhotos,
    galleryItemLinkTo,
} from "@/mk2/apps/wiki/gallery";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ArticleExperiencePhoto.mscss";
import "./ArticleExperiencePhoto.scss";

export class ArticleExperiencePhoto extends React.Component<Props> {
    private galleryItems = cacheLast<ArticleGalleryItem[]>(true);

    private getGalleryItems(): ArticleGalleryItem[] {
        const { article, experience, experiencePhoto } = this.props;
        const items = this.galleryItems(
            () => buildArticleGalleryPhotos(article),
            article.photos,
            article.experiences,
        );
        // The public merged gallery only contains approved experiences. When the
        // viewed photo isn't in it (e.g. a still-pending experience, yet linked
        // from its own review card), fall back to just this experience's own
        // photos so the direct link still navigates.
        if (items.some((item) => item.photofile.id === experiencePhoto.photofile.id)) {
            return items;
        }
        return (experience.photos ?? []).map((expPhoto) => ({
            photofile: expPhoto.photofile,
            experienceId: experience.id,
        }));
    }

    public render() {
        const { loadingState, article } = this.props;

        return (
            <div className={styles.ArticleExperiencePhoto}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!article}
                />
            </div>
        );
    }

    private renderSuccess = () => {
        const { experiencePhoto } = this.props;
        const items = this.getGalleryItems();

        const currentIndex: number = items.findIndex(
            (item) => item.photofile.id === experiencePhoto.photofile.id,
        );

        const galleryPhotos: ReactImageGalleryItem[] = items.map(({ photofile }) => ({
            original: photoUrl(photofile, "s738x738"),
            thumbnail: photoUrl(photofile, "s150x150"),
            fullSizeUrl: photoUrl(photofile, "s1600x1600"),
        }));

        return (
            <>
                <div className={styles.ArticleExperiencePhoto__gallery}>
                    <ImageGalleryLoadable
                        items={galleryPhotos}
                        showIndex
                        showThumbnails={false}
                        autoPlay={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        slideDuration={500}
                        startIndex={currentIndex}
                        onSlide={this.onSlide}
                        renderLeftNav={this.renderLeftNav}
                        renderRightNav={this.renderRightNav}
                        renderCustomControls={this.renderCustomControls}
                    />
                </div>
            </>
        );
    };

    private renderLeftNav = (onClick, disabled) => {
        return (
            <div
                key="prev"
                className={styles.ArticleExperiencePhoto__prev}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={styles.ArticleExperiencePhoto__prev__icon}
                />
            </div>
        );
    };

    private renderRightNav = (onClick, disabled) => {
        return (
            <div
                key="next"
                className={styles.ArticleExperiencePhoto__next}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.ArticleExperiencePhoto__next__icon}
                />
            </div>
        );
    };

    private renderCustomControls = () => {
        const { photoId } = this.props;
        const item = this.getGalleryItems().find((i) => i.photofile.id === photoId);
        const photo = item?.photofile;
        const showMagnifier = photo?.versions?.includes("s1600x1600");
        if (!showMagnifier) {
            return null;
        }

        return (
            <div className={styles.ArticleExperiencePhoto__controls}>
                {showMagnifier && (
                    <a
                        data-google-interstitial="false"
                        key="magnifier"
                        href={photoUrl(photo, "s1600x1600")}
                        target="_blank"
                        rel="noopener"
                        className={styles.ArticleExperiencePhoto__controls__control}
                    >
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </a>
                )}
            </div>
        );
    };

    private onSlide = (currentIndex: number) => {
        const { article, routeNavigate } = this.props;
        const item = this.getGalleryItems()[currentIndex];
        routeNavigate(galleryItemLinkTo(article, item), { replace: true });
    };

    private load = () => {
        const {
            routeParams: { categorySlug, articleSlug },
            experienceId,
            photoId,
            onLoad,
        } = this.props;
        onLoad(articleSlug, categorySlug, experienceId, photoId);
    };
}
